import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    Chip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";
import { useNotify } from "react-admin";
import { API } from "../../Constant";
import { getConfigGet, getConfigPatch, getConfigPost } from "../../getConfig";

const SHOP_GRID = API + "/admin/shop-grid";

interface Row {
    placement_id: number | null;
    brand: string;
    position: string;
    removed: boolean;
    _origPos: string; // "" if not originally featured
}

const FeaturedManager = () => {
    const notify = useNotify();
    const [rows, setRows] = useState<Row[]>([]);
    const [catalog, setCatalog] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [addSearch, setAddSearch] = useState("");

    const fetchAll = async () => {
        try {
            setLoading(true);
            const [feat, cat]: any[] = await Promise.all([
                axios.get(SHOP_GRID, getConfigGet({ resource: "featured" })),
                axios.get(SHOP_GRID, getConfigGet({ resource: "catalog" })),
            ]);
            const featured: Row[] = (feat.data?.data || feat.data || []).map((p: any) => ({
                placement_id: p.placement_id,
                brand: p.brand,
                position: String(p.featured_position),
                removed: false,
                _origPos: String(p.featured_position),
            }));
            setRows(featured);
            setCatalog(Array.isArray(cat.data) ? cat.data : cat.data?.data || []);
        } catch (e) {
            notify("Failed to load featured brands", "warning");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setPos = (i: number, v: string) =>
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, position: v } : r)));
    const toggleRemove = (i: number) =>
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, removed: !r.removed } : r)));

    const addBrand = (brand: string) => {
        const nextPos = Math.max(0, ...rows.filter((r) => !r.removed).map((r) => Number(r.position) || 0)) + 1;
        setRows((prev) => [
            ...prev,
            { placement_id: null, brand, position: String(nextPos), removed: false, _origPos: "" },
        ]);
        setAddOpen(false);
        setAddSearch("");
    };

    const isDirty = (r: Row) =>
        r.removed ? r._origPos !== "" : r.position !== r._origPos;
    const dirtyCount = rows.filter(isDirty).length;

    const save = async () => {
        setSaving(true);
        try {
            for (const r of rows.filter(isDirty)) {
                const featured_position = r.removed ? null : Number(r.position);
                if (r.placement_id) {
                    await axios.patch(
                        `${SHOP_GRID}/${r.placement_id}?resource=placement`,
                        { featured_position },
                        { headers: getConfigPatch() }
                    );
                } else if (!r.removed) {
                    await axios.post(
                        SHOP_GRID,
                        { resource: "placement", brand: r.brand, featured_position },
                        { headers: getConfigPost() }
                    );
                }
            }
            notify("Featured saved", "info");
            fetchAll();
        } catch (e: any) {
            notify(e?.response?.data?.message || "Failed to save featured", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress size={24} />;

    const featuredNames = new Set(rows.filter((r) => !r.removed).map((r) => r.brand.toLowerCase()));
    const addCandidates = catalog
        .filter((b) => b.brand.toLowerCase().includes(addSearch.toLowerCase()))
        .filter((b) => !featuredNames.has(b.brand.toLowerCase()))
        .slice(0, 100);

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Featured brands ({rows.filter((r) => !r.removed).length})</Typography>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    disabled={saving || dirtyCount === 0}
                    onClick={save}
                >
                    Save{dirtyCount ? ` (${dirtyCount})` : ""}
                </Button>
            </Box>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Position</TableCell>
                        <TableCell>Brand</TableCell>
                        <TableCell align="right">Remove</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.brand + i} style={r.removed ? { opacity: 0.4 } : {}}>
                            <TableCell>
                                <TextField
                                    type="number"
                                    size="small"
                                    style={{ width: 70 }}
                                    value={r.position}
                                    disabled={r.removed}
                                    onChange={(e) => setPos(i, e.target.value)}
                                />
                            </TableCell>
                            <TableCell>{r.brand}</TableCell>
                            <TableCell align="right">
                                <IconButton size="small" onClick={() => toggleRemove(i)}>
                                    <DeleteIcon fontSize="small" color={r.removed ? "disabled" : "error"} />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box mt={1}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                    Add featured brand
                </Button>
            </Box>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Add featured brand</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search brands..."
                        value={addSearch}
                        onChange={(e) => setAddSearch(e.target.value)}
                        autoFocus
                    />
                    <List dense style={{ maxHeight: 360, overflow: "auto" }}>
                        {addCandidates.map((b) => (
                            <ListItem button key={b.provider + b.brand} onClick={() => addBrand(b.brand)}>
                                <ListItemText primary={b.brand} secondary={`${b.provider} · ${b.status}`} />
                                <Chip size="small" variant="outlined" label="add" />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default FeaturedManager;
